import m from "mithril";
import Toast, { showConfirmation } from "../../js/toaster.js";
import { formatRupiah, formatNumber } from "../../utils.js";

const ITEMS_PER_PAGE = 8;

/**
 * Reusable Section Component
 */
const TransactionSection = {
    oninit: function (vnode) {
        this.currentPage = 1;
        this.expanded = true;
    },

    view: function (vnode) {
        const { title, transactions, selectedIds, onToggleSelect, onToggleSelectAll, filter } = vnode.attrs;

        // Apply filter
        const filteredTx = transactions.filter(tx =>
            filter === "" ||
            (tx.reference && tx.reference.toLowerCase().includes(filter.toLowerCase())) ||
            (tx.description && tx.description.toLowerCase().includes(filter.toLowerCase()))
        );

        // Pagination
        const totalPages = Math.ceil(filteredTx.length / ITEMS_PER_PAGE);
        const startIdx = (this.currentPage - 1) * ITEMS_PER_PAGE;
        const endIdx = startIdx + ITEMS_PER_PAGE;
        const paginatedTx = filteredTx.slice(startIdx, endIdx);

        const canPostCount = filteredTx.filter(tx => tx.preview && tx.preview.can_post).length;
        const postableTx = paginatedTx.filter(tx => tx.preview && tx.preview.can_post);
        const allSelected = postableTx.length > 0 && postableTx.every(tx => selectedIds.has(tx.id + '_' + tx.type));

        return m("div", { class: "bg-white dark:bg-gray-800 rounded-lg shadow mb-6 overflow-hidden" }, [
            // Section Header
            m("div", {
                class: "p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center cursor-pointer bg-gray-50 dark:bg-gray-700",
                onclick: () => this.expanded = !this.expanded
            }, [
                m("div", { class: "flex items-center" }, [
                    m("i", { class: `ri-arrow-${this.expanded ? 'down' : 'right'}-s-line mr-2 text-lg` }),
                    m("h2", { class: "text-lg font-semibold text-gray-800 dark:text-white" }, title),
                    m("span", { class: "ml-3 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full" }, filteredTx.length)
                ]),
                m("div", { class: "text-sm text-gray-500" },
                    canPostCount > 0 ? `${canPostCount} siap posting` : "Semua perlu mapping"
                )
            ]),

            // Section Content (Table + Pagination)
            this.expanded && m("div", [
                // Table
                m("div", { class: "overflow-x-auto" },
                    m("table", { class: "min-w-full divide-y divide-gray-200 dark:divide-gray-700" }, [
                        m("thead", { class: "bg-gray-50 dark:bg-gray-700" }, [
                            m("tr", [
                                m("th", { class: "px-4 py-3 w-4" },
                                    m("input", {
                                        type: "checkbox",
                                        checked: allSelected,
                                        onchange: () => onToggleSelectAll(paginatedTx),
                                        disabled: postableTx.length === 0
                                    })
                                ),
                                m("th", { class: "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" }, "Tanggal & Ref"),
                                m("th", { class: "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" }, "Keterangan"),
                                m("th", { class: "px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase" }, "Preview Jurnal"),
                                m("th", { class: "px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase" }, "Total"),
                                m("th", { class: "px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase" }, "Status")
                            ])
                        ]),
                        m("tbody", { class: "divide-y divide-gray-200 dark:divide-gray-700" },
                            paginatedTx.length === 0 ?
                                m("tr", m("td", { colspan: 6, class: "px-4 py-8 text-center text-gray-500" }, "Tidak ada transaksi")) :
                                paginatedTx.map(tx => {
                                    const key = tx.id + '_' + tx.type;
                                    const isSelected = selectedIds.has(key);
                                    const hasError = !tx.preview || !tx.preview.can_post;

                                    return m("tr", { key, class: isSelected ? "bg-blue-50 dark:bg-blue-900/20" : "" }, [
                                        m("td", { class: "px-4 py-3" },
                                            m("input", {
                                                type: "checkbox",
                                                checked: isSelected,
                                                onclick: () => onToggleSelect(tx),
                                                disabled: hasError
                                            })
                                        ),
                                        m("td", { class: "px-4 py-3 text-sm" }, [
                                            m("div", { class: "font-medium text-gray-900 dark:text-white" }, new Date(tx.date).toLocaleDateString('id-ID')),
                                            m("div", { class: "text-gray-500 text-xs" }, tx.reference),
                                            m("div", { class: "text-blue-600 text-xs mt-1" }, tx.display_type)
                                        ]),
                                        m("td", { class: "px-4 py-3 text-sm text-gray-600 dark:text-gray-300 max-w-xs truncate" }, tx.description || "-"),
                                        m("td", { class: "px-4 py-3" },
                                            m("div", { class: "text-xs space-y-1" },
                                                tx.preview && tx.preview.entries ? tx.preview.entries.map(e =>
                                                    m("div", { class: "flex justify-between" }, [
                                                        m("span", { class: "text-gray-500" }, `${e.name} (${e.code || e.coaCode || '?'})`),
                                                        m("span", { class: e.debit > 0 ? "text-gray-700 font-medium" : "text-gray-400" },
                                                            e.debit > 0 ? `Dr ${formatNumber(e.debit)}` : `Cr ${formatNumber(e.credit)}`
                                                        )
                                                    ])
                                                ) : null
                                            )
                                        ),
                                        m("td", { class: "px-4 py-3 text-right font-medium text-gray-900 dark:text-white" },
                                            formatNumber(tx.amount)
                                        ),
                                        m("td", { class: "px-4 py-3 text-center" },
                                            hasError ?
                                                m("span", {
                                                    class: "px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800",
                                                    title: tx.preview && tx.preview.validation_errors ? tx.preview.validation_errors.join(", ") : "Mapping Error"
                                                }, "Error Mapping") :
                                                m("span", { class: "px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800" }, "Siap")
                                        )
                                    ]);
                                })
                        )
                    ])
                ),

                // Pagination
                totalPages > 1 && m("div", { class: "px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center" }, [
                    m("span", { class: "text-sm text-gray-500" },
                        `Halaman ${this.currentPage} dari ${totalPages}`
                    ),
                    m("div", { class: "flex space-x-2" }, [
                        m("button", {
                            class: `px-3 py-1 text-sm rounded ${this.currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`,
                            onclick: () => { if (this.currentPage > 1) this.currentPage--; },
                            disabled: this.currentPage === 1
                        }, "Sebelumnya"),
                        m("button", {
                            class: `px-3 py-1 text-sm rounded ${this.currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`,
                            onclick: () => { if (this.currentPage < totalPages) this.currentPage++; },
                            disabled: this.currentPage === totalPages
                        }, "Berikutnya")
                    ])
                ])
            ])
        ]);
    }
};


/**
 * Main Posting Jurnal Page
 */
const PostingJurnal = {
    oninit: function (vnode) {
        // Initialize state
        vnode.state.transactions = [];
        vnode.state.selectedIds = new Set();
        vnode.state.loading = false;
        vnode.state.posting = false;
        vnode.state.filter = "";

        // Bind all methods to vnode.state
        vnode.state.loadTransactions = PostingJurnal.loadTransactions.bind(vnode.state);
        vnode.state.toggleSelect = PostingJurnal.toggleSelect.bind(vnode.state);
        vnode.state.toggleSelectAll = PostingJurnal.toggleSelectAll.bind(vnode.state);
        vnode.state.postSelected = PostingJurnal.postSelected.bind(vnode.state);
        vnode.state.getSavingsTransactions = PostingJurnal.getSavingsTransactions.bind(vnode.state);
        vnode.state.getLoanTransactions = PostingJurnal.getLoanTransactions.bind(vnode.state);
        vnode.state.getOtherTransactions = PostingJurnal.getOtherTransactions.bind(vnode.state);

        vnode.state.loadTransactions(vnode);
    },

    loadTransactions: async function (vnode) {
        vnode.state.loading = true;
        m.redraw();
        try {
            const response = await fetch('/api/accounting/unposted', {
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                vnode.state.transactions = data.data || [];
                console.log('Unposted data loaded:', vnode.state.transactions.length, 'items');
            } else {
                Toast.error(data.message || "Gagal memuat transaksi");
            }
        } catch (error) {
            console.error("Failed to load transactions:", error);
            Toast.error("Gagal memuat transaksi");
        } finally {
            vnode.state.loading = false;
            m.redraw();
        }
    },

    toggleSelect: function (vnode, tx) {
        const key = tx.id + '_' + tx.type;
        if (vnode.state.selectedIds.has(key)) {
            vnode.state.selectedIds.delete(key);
        } else {
            vnode.state.selectedIds.add(key);
        }
    },

    toggleSelectAll: function (vnode, transactions) {
        const postableTx = transactions.filter(tx => tx.preview && tx.preview.can_post);
        const allSelected = postableTx.length > 0 && postableTx.every(tx => vnode.state.selectedIds.has(tx.id + '_' + tx.type));

        if (allSelected) {
            transactions.forEach(tx => {
                vnode.state.selectedIds.delete(tx.id + '_' + tx.type);
            });
        } else {
            transactions.forEach(tx => {
                if (tx.preview && tx.preview.can_post) {
                    vnode.state.selectedIds.add(tx.id + '_' + tx.type);
                }
            });
        }
    },

    postSelected: async function (vnode) {
        if (vnode.state.selectedIds.size === 0) return;

        showConfirmation(`Posting ${vnode.state.selectedIds.size} transaksi ke Jurnal Umum?`, async () => {
            vnode.state.posting = true;
            m.redraw();

            try {
                const payload = vnode.state.transactions
                    .filter(tx => vnode.state.selectedIds.has(tx.id + '_' + tx.type))
                    .map(tx => ({ id: tx.id, type: tx.type }));

                const response = await fetch('/api/accounting/post-transactions', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ transactions: payload }),
                    credentials: 'include'
                });

                const data = await response.json();
                if (data.success) {
                    const { success, failed, errors } = data.results;
                    if (failed === 0) {
                        Toast.success(`${success} transaksi berhasil diposting.`);
                    } else {
                        Toast.warning(`${success} berhasil, ${failed} gagal.`);
                        console.error("Posting errors:", errors);
                    }
                    vnode.state.selectedIds.clear();
                    // Clear matched entries from the current local list immediately
                    const postedKeys = new Set(payload.map(p => p.id + '_' + p.type));
                    vnode.state.transactions = vnode.state.transactions.filter(tx => !postedKeys.has(tx.id + '_' + tx.type));

                    vnode.state.loadTransactions(vnode);
                } else {
                    Toast.error(data.message || "Gagal memposting transaksi");
                }

            } catch (error) {
                console.error("Error posting:", error);
                Toast.error("Terjadi kesalahan sistem");
            } finally {
                vnode.state.posting = false;
                m.redraw();
            }
        });
    },

    // Categorize transactions
    getSavingsTransactions: function (vnode) {
        return (vnode.state.transactions || []).filter(tx => tx.type === 'savings_transaction' || tx.type === 'savings');
    },

    getLoanTransactions: function (vnode) {
        return (vnode.state.transactions || []).filter(tx =>
            tx.type === 'loan_installment' ||
            tx.type === 'loan_payment' ||
            tx.type === 'loan_disbursement' ||
            tx.type === 'loan_settlement'
        );
    },

    getOtherTransactions: function (vnode) {
        const knownTypes = [
            'savings_transaction', 'savings',
            'loan_installment', 'loan_payment', 'loan_disbursement', 'loan_settlement'
        ];
        return (vnode.state.transactions || []).filter(tx => !knownTypes.includes(tx.type));
    },

    view: function (vnode) {
        const savingsTx = vnode.state.getSavingsTransactions(vnode);
        const loansTx = vnode.state.getLoanTransactions(vnode);
        const othersTx = vnode.state.getOtherTransactions(vnode);

        return m("div", { class: "p-6" }, [
            // Header
            m("div", { class: "flex justify-between items-center mb-6" }, [
                m("h1", { class: "text-2xl font-bold text-gray-800 dark:text-white" }, "Posting Jurnal Akuntansi"),
                m("button", {
                    class: `px-6 py-2 rounded text-white ${vnode.state.selectedIds.size > 0 ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'}`,
                    onclick: () => vnode.state.postSelected(vnode),
                    disabled: vnode.state.posting || vnode.state.selectedIds.size === 0
                }, vnode.state.posting ? "Memposting..." : `Posting (${vnode.state.selectedIds.size})`)
            ]),

            // Filter
            m("div", { class: "mb-4" }, [
                m("input", {
                    type: "text",
                    class: "p-2 border border-gray-300 rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 w-full max-w-md",
                    placeholder: "Cari transaksi...",
                    value: vnode.state.filter,
                    oninput: (e) => vnode.state.filter = e.target.value
                })
            ]),

            // Loading
            vnode.state.loading ?
                m("div", { class: "text-center py-10" },
                    m("div", { class: "inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" })
                ) :
                [
                    // Section 1: Transaksi Simpanan
                    m(TransactionSection, {
                        key: "savings",
                        title: "Transaksi Simpanan",
                        transactions: savingsTx,
                        selectedIds: vnode.state.selectedIds,
                        filter: vnode.state.filter,
                        onToggleSelect: (tx) => vnode.state.toggleSelect(vnode, tx),
                        onToggleSelectAll: (txList) => vnode.state.toggleSelectAll(vnode, txList)
                    }),

                    // Section 2: Transaksi Pinjaman
                    m(TransactionSection, {
                        key: "loans",
                        title: "Transaksi Pinjaman",
                        transactions: loansTx,
                        selectedIds: vnode.state.selectedIds,
                        filter: vnode.state.filter,
                        onToggleSelect: (tx) => vnode.state.toggleSelect(vnode, tx),
                        onToggleSelectAll: (txList) => vnode.state.toggleSelectAll(vnode, txList)
                    }),

                    // Section 3: Lainnya
                    m(TransactionSection, {
                        key: "others",
                        title: "Transaksi Lainnya",
                        transactions: othersTx,
                        selectedIds: vnode.state.selectedIds,
                        filter: vnode.state.filter,
                        onToggleSelect: (tx) => vnode.state.toggleSelect(vnode, tx),
                        onToggleSelectAll: (txList) => vnode.state.toggleSelectAll(vnode, txList)
                    })
                ]
        ]);
    }
};

export default PostingJurnal;
